import { useRef, useState, useEffect } from "react";
import Select from 'react-select';
import { Modal } from 'react-responsive-modal';
import makeAnimated from 'react-select/animated'
import { useCollectionContext } from "../context/CollectionContext";
import { db } from "../db";
import Swal from "sweetalert2";
import { updateImageTags } from "../api-helper";
import { useAccountContext } from "../context/AccountContext";
import { isUndefined } from "cropperjs";
import { ReactComponent as CloseIcon } from "../images/icons/white-close.svg";
import { useWindowSize } from "../hooks/useWindowSize";
import electronBridge from "../utils/electronBridge";
import { getAudioMimeFromBase64 } from "../utils/audioUtils";
import { ReactComponent as DoubleRightIcon } from "../images/icons/white-double-right.svg";
import { ReactComponent as DoubleLeftIcon } from "../images/icons/white-double-left.svg";
import '@google/model-viewer';

const animatedComponents = makeAnimated()

export default function FullMediaViewer(props){
    const collectionContext = useCollectionContext()
    const accountContext = useAccountContext()
    const { effectiveWidth, effectiveHeight } = useWindowSize(true)

    const mediaContainerRef = useRef(null);
    const [transform, setTransform] = useState({
      scale: 1,
      translateX: 0,
      translateY: 0,
    });
    const [isDragging, setIsDragging] = useState(false);
    const [lastMousePosition, setLastMousePosition] = useState({ x: 0, y: 0 });
    const [open, setOpen] = useState(false)
    const [options, setOptions] = useState([]);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [inputTag, setInputTag] = useState("");
    const [tag, setTag] = useState(props.tag)
    const [tagLoading, setTagLoading] = useState(false);

    const dataType = props.dataType || 'image';
    const isImage = dataType === 'image' || !dataType;
    const isVideo = dataType === 'video';
    const isAudio = dataType === 'audio';
    const is3D = dataType === '3d';

    const handleWheel = (e) => {
        if (!isImage) return;
        e.preventDefault();

        const rect = mediaContainerRef.current.getBoundingClientRect();
        const cx = e.clientX - rect.left;
        const cy = e.clientY - rect.top;

        const step = 1.1;
        const k = e.deltaY < 0 ? step : 1 / step;

        const oldScale = transform.scale;
        const newScale = Math.min(Math.max(oldScale * k, 0.1), 10);

        const px = (cx - transform.translateX) / oldScale;
        const py = (cy - transform.translateY) / oldScale;

        const newTranslateX = cx - px * newScale;
        const newTranslateY = cy - py * newScale;

        const newTransform = { scale: newScale, translateX: newTranslateX, translateY: newTranslateY };
        setTransform(newTransform);
        localStorage.setItem('imageViewerTransform', JSON.stringify(newTransform));
      };

    const toFileUrl = (filePath) => {
        return filePath;
      }

    useEffect(() => {
        const handleResize = () => {
            const savedTransform = localStorage.getItem('imageViewerTransform');
            if (savedTransform) {
                setTransform(JSON.parse(savedTransform));
            }
        };

        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const handleMouseDown = (e) => {
      if (!isImage) return;
      setIsDragging(true);
      setLastMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e) => {
      if (!isDragging || !isImage) return;

      const { x, y } = lastMousePosition;
      const deltaX = e.clientX - x;
      const deltaY = e.clientY - y;

      const newTransform = {
        ...transform,
        translateX: transform.translateX + deltaX,
        translateY: transform.translateY + deltaY,
      };

      setTransform(newTransform);
      localStorage.setItem('imageViewerTransform', JSON.stringify(newTransform));
      setLastMousePosition({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    const imageStyle = {
      transform: `scale(${transform.scale}) translate(${transform.translateX / transform.scale}px, ${transform.translateY / transform.scale}px)`,
      transformOrigin: '0 0',
      transition: isDragging ? 'none' : 'transform 0.1s ease-out',
      cursor: isDragging ? 'grabbing' : 'grab',
    };

    const openTagModal = () => {
        setOptions([]);

        const combinedTags = [...collectionContext.tags, ...collectionContext.collectionTags];
        const uniqueTags = [...new Set(combinedTags)];

        const sortedTags = uniqueTags.sort((a, b) => a.localeCompare(b));
        setOptions(sortedTags.map(tag => ({ value: tag, label: tag })));
        setOpen(true);
    }

    const editTagOnImage = () => {
        setTagLoading(true);
        let multiOptions = "";
        let initTag
        let concate

        setTimeout(() => {
            if (isUndefined(props.tag) == false) {
                initTag = props.tag + ","
            } else {
                initTag = "";
            }
            if (selectedOptions !== "" && !selectedOptions.includes("undefined")) {
                selectedOptions.map((arrValue) => {
                    if (!initTag.includes(arrValue.value)){
                        multiOptions += arrValue.value + ",";
                    }
                })
                initTag += multiOptions;
            }


        }, 1000)

        setTimeout(() => {
            if (inputTag !== "") {
                if (!initTag.includes(inputTag)){
                    concate = initTag + inputTag
                } else {
                    concate = initTag
                }
            } else {
                concate = initTag;
            }
            if (concate.endsWith(',')) {
                concate = concate.slice(0, -1);
            }
            if (concate.startsWith(',')) {
                concate = concate.substring(1);
            }
            if (concate == '') {
                Swal.fire({
                    color: "#190A4E",
                    title: "Tag Empty",
                    text: "You cannot tag media with an empty tag. Please add a tag before proceeding.",
                    icon: "info",
                    timer: 2000,
                    timerProgressBar: true,
                    showConfirmButton: false
                })
                setTagLoading(false);
                return;
            }
            setTag(concate)
            db.images.update(props.id, {tag: concate}).then(async(updated) => {
                if (updated) {
                    if (props.isMyCollection) {
                        let generatedImages = [];
                        await db.images.reverse().toArray((images) => {
                            generatedImages = images;
                        });
                        let imageInImagesTable;
                        generatedImages.forEach((image) => {
                            if (image.hash === props.hash) {
                                imageInImagesTable = image;
                            }
                        })
                        if (imageInImagesTable) {
                            db.images.update(imageInImagesTable.id, { tag: concate });
                        } else {
                            console.log("Media not found in the images table");
                        }
                        console.log("Update Media Tags Route: ", props.hash);
                        updateImageTags(accountContext.accountDetails.accountNo, props.hash, concate)

                    } else {
                        let collectionImages = [];
                        await db.collectedImages.reverse().toArray((images) => {
                            collectionImages = images;
                        });
                        let imageInCollectionTable;
                        collectionImages.forEach((image) => {
                            if (image.hash === props.hash) {
                                imageInCollectionTable = image;
                            }
                        })
                        if (imageInCollectionTable) {
                            db.collectedImages.update(imageInCollectionTable.id, { tag: concate });
                        } else {
                            console.log("Media not found in the collection table");
                        }
                    }
                    Swal.fire({
                        title:"Success",
                        text:"Your media was successfully tagged",
                        icon:"success",
                        timer: 1000,
                        showConfirmButton: false
                    }).then(() => {
                        setTagLoading(false);
                        setInputTag("");
                        setSelectedOptions([]);
                        props.setTag?.(concate)
                    })
                    setOpen(false)
                }
            })

        }, 2500)

    }

    const handleKeyDown = (e) => {
        if (e.key === "Escape") props.closeMediaViewer?.();
        if (e.key === "ArrowLeft") props.onPrev?.();
        if (e.key === "ArrowRight") props.onNext?.();

        if (e.key === "Enter") {
            editTagOnImage();
        }

        if (e.key === "Escape") {
            props.closeMediaViewer?.();
        }

        if (e.ctrlKey && isImage) {
            if (e.key === '+' || e.key === '=' || e.code === 'NumpadAdd') {
                e.preventDefault();

                const simulatedEvent = {
                    preventDefault: () => {},
                    currentTarget: mediaContainerRef.current,
                    deltaY: -100,
                    clientX: effectiveWidth / 2,
                    clientY: effectiveHeight / 2
                };

                handleWheel(simulatedEvent);
                return false;
            } else if (e.key === '-' || e.code === 'NumpadSubtract') {
                e.preventDefault();

                const simulatedEvent = {
                    preventDefault: () => {},
                    currentTarget: mediaContainerRef.current,
                    deltaY: 100,
                    clientX: effectiveWidth / 2,
                    clientY: effectiveHeight / 2
                };

                handleWheel(simulatedEvent);
                return false;
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [transform]);

    // Attach wheel listener with { passive: false } so preventDefault works
    useEffect(() => {
        if (!isImage) return;
        const el = mediaContainerRef.current;
        if (!el) return;
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => el.removeEventListener('wheel', handleWheel);
    }, [transform, isImage]);

    useEffect(() => {
        if (!isImage) return;
        const preload = (src, type) => {
            if (!src || type !== 'image') return;
            const i = new Image();
            i.src = src.includes(':\\')
                ? toFileUrl(src)
                : (src.startsWith('data:') || src.startsWith('http') ? src : `data:image/*;base64,${src}`);
            };
            preload(props.prevMediaSrc, props.prevDataType);
            preload(props.nextMediaSrc, props.nextDataType);
        }, [props.prevMediaSrc, props.nextMediaSrc, props.prevDataType, props.nextDataType]);

        const defaultTransform = { scale: 1, translateX: 0, translateY: 0 };
        const resetTransform = () => {
        setTransform(defaultTransform);
        localStorage.setItem('imageViewerTransform', JSON.stringify(defaultTransform));
    };

    const getMediaSrc = (media) => {
        if (!media) return null;
        if (electronBridge.isElectron && media.includes(":\\")) {
            return toFileUrl(media);
        } else if (media.startsWith('http')) {
            return media;
        } else if (media.startsWith('data:')) {
            return media;
        } else {
            if (isVideo) return `data:video/mp4;base64,${media}`;
            if (isAudio) return `data:${getAudioMimeFromBase64(media)};base64,${media}`;
            if (is3D) return `data:model/gltf-binary;base64,${media}`;
            return `data:image/png;base64,${media}`;
        }
    };

    const mediaSrc = getMediaSrc(props.image);

    const getMediaTypeLabel = () => {
        if (isVideo) return 'Video';
        if (isAudio) return 'Audio';
        if (is3D) return '3D Model';
        return 'Image';
    };

    const renderMedia = () => {
        if (isVideo) {
            return (
                <video
                    className="relative max-w-[95vw] max-h-[90vh] object-contain select-none"
                    src={mediaSrc}
                    controls
                    autoPlay
                    loop
                />
            );
        }

        if (isAudio) {
            return (
                <div className="flex flex-col items-center justify-center gap-6 p-8 bg-dark-blue/50 rounded-xl">
                    <div className="text-6xl">🎵</div>
                    <p className="text-white text-xl font-semibold">Audio File</p>
                    <audio
                        className="w-80"
                        src={mediaSrc}
                        controls
                        autoPlay
                    />
                    {props.prompt && (
                        <p className="text-white/70 text-sm max-w-md text-center mt-4">{props.prompt}</p>
                    )}
                </div>
            );
        }

        if (is3D) {
            return (
                <model-viewer
                    src={mediaSrc}
                    alt="3D Model"
                    auto-rotate
                    camera-controls
                    style={{ width: '80vw', height: '80vh', maxWidth: '1200px', maxHeight: '800px' }}
                    className="rounded-lg"
                />
            );
        }

        return (
            <img
                className="relative max-w-[95vw] max-h-[90vh] object-contain select-none"
                src={mediaSrc}
                alt={`Generated image: ${props.seed}`}
                style={imageStyle}
                draggable={false}
            />
        );
    };

    return (
        <div
            className="fixed inset-0 z-[10001] bg-gray-900/80"
            tabIndex={0}
        >
            <div
            ref={mediaContainerRef}
            className="relative w-full h-full flex items-center justify-center overflow-hidden"
            onMouseDown={isImage ? handleMouseDown : undefined}
            onMouseMove={isImage ? handleMouseMove : undefined}
            onMouseUp={isImage ? handleMouseUp : undefined}
            onMouseLeave={isImage ? handleMouseUp : undefined}
            >
                {renderMedia()}
                <div className={"w-full absolute right-0 top-0 flex flex-row justify-end items-center justify-self-start z-[999]"} onKeyDown={(e) => handleKeyDown(e)}>
                        <button className={"p-3 text-white m-2 me-8 hover:text-yellow transition bg-yellow"} onClick={props.closeMediaViewer}>
                            <CloseIcon alt='Close Media Viewer' className='h-7'/>
                        </button>
                </div>
                {props.total > 1 && props.onPrev && (
                <>
                    <button
                    className="absolute left-0 top-0 h-full w-18 flex items-center justify-start text-white/80 hover:text-white"
                    onClick={() => { resetTransform(); props.onPrev(); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label="Previous media"
                    title="Previous (←)"
                    >
                        <DoubleLeftIcon />
                    </button>
                </>
                )}

                {props.total > 1 && props.onNext && (
                <>
                    <button
                    className="absolute right-0 top-0 h-full w-18 flex items-center justify-end text-white/80 hover:text-white"
                    onClick={() => { resetTransform(); props.onNext(); }}
                    onMouseDown={(e) => e.stopPropagation()}
                    aria-label="Next media"
                    title="Next (→)"
                    >
                        <DoubleRightIcon />
                    </button>
                </>
                )}

                {typeof props.currentIndex === 'number' && typeof props.total === 'number' && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 rounded-full bg-black/70 px-3 py-1 text-xl text-white font-semibold flex items-center gap-2">
                    <span className="text-yellow text-sm">{getMediaTypeLabel()}</span>
                    <span>{props.currentIndex + 1} / {props.total}</span>
                </div>
                )}
                {props.tag ?
                    <div className={`absolute flex flex-col gap-3 group-hover:hidden bottom-0 left-0 rounded-tr px-3 py-2 bg-dark-blue/50 border-2 text-sm w-44`}>
                        <p className="text-lg font-semibold">{getMediaTypeLabel()} Tag</p>
                        <p className="text-yellow font-bold text-lg my-1">{props.tag}</p>
                        <button className="rounded-lg bg-yellow py-2 px-3 shadow-md font-bold text-m uppercase" onClick={openTagModal}>Add Tag(s)</button>
                    </div>
                :
                    <div className={`absolute flex flex-col gap-3 group-hover:hidden bottom-0 left-0 rounded-tr px-3 py-2 bg-dark-blue/50 border-2 text-sm w-44`}>
                        <p className="text-lg font-semibold">Add {getMediaTypeLabel()} Tag</p>
                        <button className="rounded-lg bg-yellow py-2 px-3 shadow-md font-bold text-m uppercase" onClick={openTagModal}>Add Tag(s)</button>
                    </div>
                }
            </div>
            <Modal open={open} onClose={() => setOpen(false)} center container={document.querySelector('.main-content') || undefined}>
                <h2 className="text-2xl mb-5 font-bold">Category</h2>
                <p>
                    Edit the tags you would like to add with the {getMediaTypeLabel().toLowerCase()}
                </p>
                <div className="flex flex-row items-center justify-between my-5">
                    <p className="place-content-start">Original Tag: <span className="font-bold">{props.tag}</span></p>
                </div>
                <div className={"p-5 my-5 border border-dark-blue rounded-lg"}>
                    <label className="block mb-2 text-sm font-medium" htmlFor="existing">Existing Category</label>
                    <Select options={options} components={animatedComponents} onChange={(e) => setSelectedOptions(e)} isMulti isClearable onKeyDown={(e) => handleKeyDown(e)}/>
                </div>
                <div className="flex flex-row items-center">
                    <hr className={"flex-grow border border-dark-blue/40 bg-dark-blue/40"} />
                    <h1 className="mx-4 font-bold text-xl">OR</h1>
                    <hr className={"flex-grow border border-dark-blue/40 bg-dark-blue/40"} />
                </div>
                    <p>
                        Type in a new category to have more ease in sorting your media
                    </p>
                    <div className={"p-5 my-5 border border-dark-blue rounded-lg"}>
                        <label className="block mb-2 text-sm font-medium" htmlFor="emailAddress">New Category</label>
                        <input type="text" name="emailAddress" className="w-full rounded-lg text-dark-blue border border-dark-blue p-2" placeholder="Category"
                        value={inputTag} onChange={(e) => setInputTag(e.target.value)} onKeyDown={(e) => handleKeyDown(e)}/>
                    </div>
                <div className="flex flex-row justify-center gap-4">
                    <button onClick={() => editTagOnImage()} className="rounded w-1/2 mx-auto mt-6 mb-3 p-2 bg-yellow text-dark-blue shadow font-bold">
                        {tagLoading ?
                            <svg aria-hidden="true" role="status" className="inline w-5 h-5 mr-3 mb-1 text-dark-blue animate-spin" viewBox="0 0 99 101" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z" fill="#ffb02e"/>
                                <path d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z" fill="currentColor"/>
                            </svg>
                        :
                            null
                        }
                        Categorize
                    </button>
                    <button onClick={() => setOpen(false)} className="rounded w-1/2 mx-auto mt-6 mb-3 p-2 bg-dark-blue text-yellow shadow font-bold">Cancel</button>
                </div>
            </Modal>
        </div>
    )
}
